import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import { v4 as uuidv4 } from 'uuid'
import { useQueryClient } from 'react-query'
import {
  useQuestionPutQuery,
  useQuestionPostQuery,
} from '../../Queries/QuestionQuery'
import { htmlToString, isQuizStarted } from '../../utils'
import { useStyles } from './AddEssayQuestionStyles'
import EditEssayQuestion from './EditEssayQuestion'
import EssayTile from './EssayTile'
import ConfirmBox from '../common/ConfirmBox'
import {
  useGetAllQuizzesForAQuestion,
  useUpdateQuestionForQuizzes,
} from '../../Queries/QuizQuery'

function AddEssayQuestion({
  questionTypeFromParent,
  handleQuestionTypeChange,
  questions,
  quizType,
  codingQuestionSwitch,
  question,
  questionIndex,
  addQuestion,
  setAddQuestion,
  reload,
  setReload,
  quizId,
  quizStartTime,
  isPublished,
  save,
  setSave,
  setCancelAddQuestion,
  setErrorMessage,
  setTruthValue,
  postQuestionMapping,
  deleteMappingQuestion,
  refetchGetQueryById,
  refetchGetQuizQuestionQueryById,
  deleteQuestion,
  isWriteAllowed,
  setLocalQuestion,
  localQuestion,
  setTextEditorQuestion,
  textEditorQuestion,
  setEditViewQuestion,
  editViewQuestion,
  deleteQuestionId,
  setDeleteQuestionId,
  editOngoing,
  setEditOngoing,
  editQuestion,
  setEditQuestion,
}) {
  const queryClient = useQueryClient()
  const classes = useStyles()
  const [openDialog, setOpenDialog] = useState(false)
  const [errors, setErrors] = useState({ emptyQuestion: false })

  const [affectedQuizes, setAffectedQuizes] = useState([])
  const [selectedQuizzes, setSelectedQuizzes] = useState([])
  const [showQuizesListModal, setShowQuizesListModal] = useState(false)

  const editQuestionQuery = useQuestionPutQuery(
    quizId,
    localQuestion?.id,
    'essay',
    {
      onSuccess: () => {
        setEditQuestion(NaN)
        setErrorMessage('Question Saved Successfully!')
        setTruthValue(true)
        setReload(reload + 1)
        refetchGetQueryById()
        refetchGetQuizQuestionQueryById()
        setLocalQuestion({})
        setEditViewQuestion({})
        setTextEditorQuestion({ question: '<p></p>' })
      },
      onError: () => {
        setErrorMessage('Failed')
      },
    }
  )

  const updateQuestionsInQuizzes = useUpdateQuestionForQuizzes({
    onSuccess: () => {
      setEditQuestion(NaN)
      setShowQuizesListModal(false)
      setErrorMessage('Question Saved Successfully!')
      setTruthValue(true)
      setAddQuestion(false)
      setReload(reload + 1)
      refetchGetQueryById()
      refetchGetQuizQuestionQueryById()
      queryClient.invalidateQueries('quizGetQueryById')
    },
  })

  const postNewQuestion = useQuestionPostQuery({
    staleTime: 0,
    retry: false,
    onSuccess: (successResponse) => {
      const id = successResponse?.data?.data?.id
      const newQuestionId = id
      updateQuestionsInQuizzes.mutate({
        oldQuestionId: localQuestion?.id,
        newQuestionId,
        type: localQuestion?.type,
        marks: localQuestion?.marks,
        quizIDs: selectedQuizzes.map((quiz) => quiz?.quizID),
      })
    },
  })

  const postQuestion = useQuestionPostQuery({
    staleTime: 0,
    onSuccess: async (successResponse) => {
      const id = successResponse?.data?.data?.id
      const questionDetailsObj = {
        questionId: id,
        type: localQuestion?.type,
      }
      postQuestionMapping.mutate(questionDetailsObj)
      setErrorMessage('Question Saved Successfully!')
      setTruthValue(true)
      setAddQuestion(false)
      setReload(reload + 1)
      setSave(false)
      await refetchGetQueryById()
      await refetchGetQuizQuestionQueryById()
    },
    onError: () => {
      setErrorMessage('Failed')
      setSave(false)
    },
  })

  const { refetch: refetchQuizzesForQuestion } = useGetAllQuizzesForAQuestion(
    localQuestion?.id,
    {
      enabled: false,
      onSuccess: (response) => {
        const filteredQuizzes = response?.filter((quiz) => {
          return quiz?.status === 'DRAFT' || quiz?.status === 'UPCOMING'
        })
        setAffectedQuizes(filteredQuizzes || [])
      },
    }
  )

  const removeId = (dummyQuestion) => {
    const modified = dummyQuestion?.choices?.map(
      ({ id, ...remainingProperties }) => {
        let returnObj
        if (typeof id === 'string' && id.includes('Added')) {
          returnObj = { ...remainingProperties }
        } else {
          returnObj = { id, ...remainingProperties }
        }
        return returnObj
      }
    )
    const temp = dummyQuestion
    temp.choices = modified
    return temp
  }

  useEffect(() => {
    if (!Number.isNaN(editQuestion)) refetchQuizzesForQuestion()
  }, [editQuestion])

  useEffect(() => {
    if (question && !addQuestion) return

    const isEditingEssay =
      editViewQuestion?.id !== undefined && editViewQuestion?.type === 'essay'

    const value = {
      id: isEditingEssay ? editViewQuestion.id : uuidv4(),
      question: isEditingEssay ? editViewQuestion.question : ' ',
      type: isEditingEssay ? editViewQuestion.type : 'essay',
      isRandomize: isEditingEssay ? editViewQuestion.isRandomize : false,
      quizID: quizId,
    }

    setLocalQuestion(value)
    setTextEditorQuestion((prev) => ({ ...prev, question: value.question }))
    setEditViewQuestion(value)
  }, [question])

  const handleChange = () => {
    if (editOngoing) {
      setErrorMessage('Please finish the previous edit')
    } else {
      setLocalQuestion(question)
      setTextEditorQuestion({ question: question?.question })
      setEditQuestion(questionIndex)
      setEditOngoing(true)
    }
  }

  const handleSave = async (value = '') => {
    const dummyLocalQuestion = JSON.parse(JSON.stringify(localQuestion))
    dummyLocalQuestion.question = textEditorQuestion.question
    dummyLocalQuestion.quizID = quizId

    if (htmlToString(dummyLocalQuestion?.question).trim() === '') {
      setErrors((errorsPrev) => {
        return { ...errorsPrev, emptyQuestion: true }
      })
      if (addQuestion) setSave(false)
      return
    }

    setErrors((errorsPrev) => {
      return { ...errorsPrev, emptyQuestion: false }
    })

    if (value === 'duplicateEdit') {
      await postNewQuestion.mutateAsync(removeId(dummyLocalQuestion))
      setEditOngoing(false)
      return
    }

    if (!addQuestion) {
      if (dummyLocalQuestion.isEditable === false) {
        deleteMappingQuestion.mutate({
          quizId,
          questionId: dummyLocalQuestion?.id,
        })
        delete dummyLocalQuestion.id
        dummyLocalQuestion.type = 'essay'
        postQuestion.mutate(dummyLocalQuestion)
      } else {
        dummyLocalQuestion.type = 'essay'
        editQuestionQuery.mutate(dummyLocalQuestion)
      }
    }

    if (addQuestion) {
      delete dummyLocalQuestion.id
      postQuestion.mutate(dummyLocalQuestion)
    }

    setEditOngoing(false)
  }

  useEffect(() => {
    if (save && addQuestion) handleSave()
  }, [save, addQuestion])

  const handleDelete = () => {
    setOpenDialog(true)
    setDeleteQuestionId(question?.id)
  }

  const handleClick = () => {
    deleteQuestion.mutate({
      quizId,
      questionId: deleteQuestionId,
      questionType: 'essay',
    })
    setOpenDialog(false)
    setDeleteQuestionId(undefined)
  }
  const handleClose = () => {
    setOpenDialog(false)
  }

  const handleCancel = () => {
    setEditOngoing(false)
    setEditQuestion(NaN)
    setAddQuestion(false)
    setLocalQuestion(question)
  }

  const handleCancelAddTf = () => {
    setEditOngoing(false)
    setCancelAddQuestion(false)
    setAddQuestion(false)
  }

  const updateQuestionInSelectedQuizzes = async () => {
    const dummyLocalQuestion = JSON.parse(JSON.stringify(localQuestion))
    dummyLocalQuestion.question = textEditorQuestion.question
    delete dummyLocalQuestion.id
    dummyLocalQuestion.quizID = quizId
    await postNewQuestion.mutateAsync(removeId(dummyLocalQuestion))
  }

  return (
    <Grid item container xs={12}>
      {editQuestion === questionIndex || addQuestion ? (
        <EditEssayQuestion
          question={question}
          addQuestion={addQuestion}
          questions={questions}
          questionIndex={questionIndex}
          localQuestion={localQuestion}
          errors={errors}
          isQuizStarted={isQuizStarted}
          quizStartTime={quizStartTime}
          isPublished={isPublished}
          questionTypeFromParent={questionTypeFromParent}
          handleQuestionTypeChange={handleQuestionTypeChange}
          quizType={quizType}
          codingQuestionSwitch={codingQuestionSwitch}
          textEditorQuestion={textEditorQuestion}
          setTextEditorQuestion={setTextEditorQuestion}
          editQuestionQuery={editQuestionQuery}
          postQuestion={postQuestion}
          editQuestion={editQuestion}
          handleCancel={handleCancel}
          handleCancelAddTf={handleCancelAddTf}
          handleSave={handleSave}
          showQuizesListModal={showQuizesListModal}
          setShowQuizesListModal={setShowQuizesListModal}
          affectedQuizes={affectedQuizes}
          updateQuestionInSelectedQuizzes={updateQuestionInSelectedQuizzes}
          setSelectedQuizzes={setSelectedQuizzes}
          selectedQuizzes={selectedQuizzes}
          quizId={quizId}
          setLocalQuestion={setLocalQuestion}
          editViewQuestion={editViewQuestion}
        />
      ) : (
        <EssayTile
          questionIndex={questionIndex}
          editQuestion={editQuestion}
          addQuestion={addQuestion}
          question={question}
          setEditQuestion={setEditQuestion}
          isPublished={isPublished}
          isQuizStarted={isQuizStarted}
          quizStartTime={quizStartTime}
          deleteQuestion={deleteQuestion}
          handleDelete={handleDelete}
          isWriteAllowed={isWriteAllowed}
          handleChange={handleChange}
        />
      )}
      <ConfirmBox
        isOpenDialog={openDialog}
        handleClose={handleClose}
        handleClick={handleClick}
        boxTitle="Delete Question"
        boxDescription="Are you sure you want to delete this question?"
        styles={classes}
        buttonName="Delete"
      />
    </Grid>
  )
}

export default AddEssayQuestion
